import {
    ONLINE_OFFER,
    OFFLINE_OFFER,
    EBON_OFFER,
    CATEGORY_OFFER_MEA_TYPE,
    SELECTION_MOMENT_MEA_TYPE,
    TARGET_ONLINE_OFFERS,
    TARGET_OFFLINE_OFFERS,
    TARGET_EBON_OFFERS,
    TARGET_CATEGORY_OFFERS,
    TARGET_MOMENT_OFFERS,
    DEFAULT_TURNOVER_ORDER
} from './constants';

import { categoryDisplayOrder, filterOnCategory, filterOnCategoryEbon } from '../partner/selector';
import {
    hasOffline,
    hasCashbackOffline,
    hasCashback,
    hasEbonCashback,
    convertPartnerNameForUrl
} from '../partner/utils';

/*
 * Complétion des MEA (mises en avant) des sélections de la home.
 *
 * Règles communes à tous les types :
 * - Les MEA paramétrés en back-office sont d'abord validés : un MEA dont le
 *   marchand est absent de son référentiel ou sans offre affichable est
 *   écarté.
 * - Si les MEA valides n'atteignent pas la cible (TARGET_* : 4 par section
 *   en grille, TARGET_CAROUSEL_OFFERS = 8 pour les sections rendues en
 *   carousel), la liste est complétée automatiquement avec les marchands les
 *   plus populaires disponibles, sans jamais proposer deux fois le même
 *   marchand.
 * - Les compléments sont des MEA synthétiques (id négatif, renvoi vers la
 *   fiche marchand) placés à la suite des MEA paramétrés (ordreAffichage).
 * - La section s'affiche donc même sans MEA paramétré, et se masque
 *   seulement si aucune offre n'est disponible.
 */

/**
 * MEA « Sélection en ligne » (type 14).
 * - MEA valide : le marchand a un cashback en ligne affichable (hasCashback).
 * - Complétion : meilleurs marchands en ligne du référentiel classique,
 *   triés par chiffre d'affaires (turnoverEbcOrder croissant).
 */
export const completeOnlineMeasWithAvailablePartners = (
    onlineMeas = [],
    partners = {},
    targetCount = TARGET_ONLINE_OFFERS
) => {
    return completeMeasWithAvailablePartners(
        onlineMeas,
        partners,
        targetCount,
        hasCashback,
        (partner, usedIds) =>
            hasCashback(partner) && partner.id !== undefined && !usedIds.has(partner.id),
        ONLINE_OFFER,
        { offline: false }
    );
};

/**
 * MEA « Sélection en magasin » (type 63).
 * - MEA valide : le marchand a une activité en magasin (hasOffline) ET une
 *   remise magasin affichable (hasCashbackOffline).
 * - Complétion : meilleurs marchands en magasin du référentiel classique,
 *   sur les mêmes critères, triés par chiffre d'affaires.
 */
export const completeOfflineMeasWithAvailablePartners = (
    offlineMeas = [],
    partners = {},
    targetCount = TARGET_OFFLINE_OFFERS
) => {
    return completeMeasWithAvailablePartners(
        offlineMeas,
        partners,
        targetCount,
        hasOffline,
        (partner, usedIds) =>
            hasOffline(partner) &&
            hasCashbackOffline(partner) &&
            partner.id !== undefined &&
            !usedIds.has(partner.id),
        OFFLINE_OFFER,
        { offline: true }
    );
};

/**
 * MEA « Sélection en bon d'achat » (type 80).
 * - MEA valide : le marchand existe dans le référentiel ebon avec une remise
 *   ebon > 0 (hasEbonCashback).
 * - Complétion : marchands ebon disponibles, dans l'ordre renvoyé par le
 *   référentiel ebon (pas de champ de chiffre d'affaires : le tri par
 *   turnoverEbcOrder est neutre pour eux).
 */
export const completeEbonMeasWithAvailablePartners = (
    ebonMeas = [],
    ebonPartners = [],
    targetCount = TARGET_EBON_OFFERS
) => {
    return completeMeasWithAvailablePartners(
        ebonMeas,
        ebonPartners,
        targetCount,
        hasEbonCashback,
        (partner, usedIds) =>
            hasEbonCashback(partner) && partner.id !== undefined && !usedIds.has(partner.id),
        EBON_OFFER,
        { ebon: true, offline: false }
    );
};

/**
 * Valide un MEA mixte (catégorie, sélection du moment...) contre son propre
 * référentiel, selon les flags offline/ebon portés par le MEA :
 * - ebon → le marchand doit avoir une remise ebon (référentiel ebon) ;
 * - offline → activité en magasin + remise magasin (référentiel classique) ;
 * - sinon → cashback en ligne affichable (référentiel classique).
 */
const isValidMixedMea = (mea, partners, ebonPartners) => {
    if (mea.ebon) {
        return hasEbonCashback(ebonPartners.find(p => p.id === mea.idPartenaire));
    }
    const partner = partners[mea.idPartenaire];
    return mea.offline ? hasOffline(partner) && hasCashbackOffline(partner) : hasCashback(partner);
};

/**
 * Complétion générique des MEA mixtes (catégorie, sélection du moment) :
 * un MEA mixte peut pointer un marchand en ligne, en magasin ou en bon
 * d'achat (flags offline/ebon du MEA).
 *
 * Règles :
 * 1. Chaque MEA paramétré est validé contre son propre référentiel
 *    (isValidMixedMea).
 * 2. La complétion parcourt les marchands dans le MÊME ordre que « les plus
 *    populaires » de l'annuaire mixte : d'abord les marchands avec un ordre
 *    imposé pour la catégorie (CATEGORY_DISPLAY_ORDER, tout type d'offre
 *    confondu), dans cet ordre ; puis les candidats de chaque référentiel
 *    triés par leur propre champ de popularité (non comparables entre eux),
 *    entrelacés par rang — 1er en ligne, 1er magasin, 1er bon d'achat, puis
 *    2e de chaque... Les deux versions d'un marchand gardent chacune leur
 *    rang dans ce parcours, comme dans l'annuaire.
 * 3. Dédoublonnage par nom normalisé (les espaces d'ids des référentiels
 *    classique et ebon diffèrent) : un marchand déjà pris — MEA paramétré ou
 *    rang plus populaire, quel que soit le référentiel — est écarté.
 * 4. Un marchand retenu est proposé dans sa version la plus prioritaire
 *    disponible : bon d'achat > en ligne > magasin, même si cette version
 *    est moins bien classée que celle qui l'a fait retenir.
 */
const completeMixedMeasWithAvailablePartners = (
    meas,
    partners,
    ebonPartners,
    targetCount,
    typeId,
    categoryId,
    extraProps
) => {
    const validMeas = meas.filter(mea => isValidMixedMea(mea, partners, ebonPartners));

    if (validMeas.length >= targetCount) {
        return validMeas;
    }

    const neededCount = targetCount - validMeas.length;

    const usedNames = new Set(
        validMeas
            .map(mea =>
                mea.ebon
                    ? ebonPartners.find(p => p.id === mea.idPartenaire)
                    : partners[mea.idPartenaire]
            )
            .filter(Boolean)
            .map(partner => convertPartnerNameForUrl(partner.name))
    );

    const partnersArray = Object.values(partners);
    const inCategory = partner => categoryId == null || filterOnCategory(categoryId)(partner);
    const inCategoryEbon = partner =>
        categoryId == null || filterOnCategoryEbon(categoryId)(partner);

    // Les candidats de chaque référentiel, triés par la popularité de leur
    // propre référentiel — les mêmes listes que celles de l'annuaire mixte.
    const onlineCandidates = partnersArray
        .filter(p => hasCashback(p) && inCategory(p))
        .sort(sortByRevenue);
    const offlineCandidates = partnersArray
        .filter(p => hasOffline(p) && hasCashbackOffline(p) && inCategory(p))
        .sort(sortByRevenueOffline);
    const ebonCandidates = ebonPartners
        .filter(p => hasEbonCashback(p) && inCategoryEbon(p))
        .sort((p1, p2) => (p1.order || 0) - (p2.order || 0));

    const indexByName = candidates => {
        const byName = new Map();
        candidates.forEach(partner => {
            if (partner.id === undefined) {
                return;
            }
            const name = convertPartnerNameForUrl(partner.name);
            if (!byName.has(name)) {
                byName.set(name, partner);
            }
        });
        return byName;
    };
    const ebonByName = indexByName(ebonCandidates);
    const onlineByName = indexByName(onlineCandidates);

    // Un marchand retenu est proposé dans la version de son référentiel le
    // plus prioritaire : bon d'achat > en ligne > magasin — même si cette
    // version est moins bien classée que celle qui l'a fait retenir.
    const toPriorityVersion = (partner, flags) => {
        const name = convertPartnerNameForUrl(partner.name);
        if (!flags.ebon && ebonByName.has(name)) {
            return { partner: ebonByName.get(name), flags: { offline: false, ebon: true } };
        }
        if (flags.offline && onlineByName.has(name)) {
            return { partner: onlineByName.get(name), flags: { offline: false, ebon: false } };
        }
        return { partner, flags };
    };

    // Parcours dans l'ordre des plus populaires de l'annuaire mixte :
    // entrelacement par rang (1er en ligne, 1er magasin, 1er bon d'achat,
    // puis 2e de chaque...). Un marchand déjà pris (MEA paramétré ou rang
    // plus populaire, quel que soit le référentiel) est écarté.
    const pools = [
        { candidates: onlineCandidates, flags: { offline: false, ebon: false } },
        { candidates: offlineCandidates, flags: { offline: true, ebon: false } },
        { candidates: ebonCandidates, flags: { offline: false, ebon: true } }
    ];
    const additions = [];
    const tryAdd = (partner, flags) => {
        if (additions.length >= neededCount || !partner || partner.id === undefined) {
            return;
        }
        const name = convertPartnerNameForUrl(partner.name);
        if (usedNames.has(name)) {
            return;
        }
        usedNames.add(name);
        additions.push(toPriorityVersion(partner, flags));
    };

    // Marchands avec un ordre imposé pour la catégorie (CATEGORY_DISPLAY_ORDER,
    // tout type d'offre confondu) : piochés en premier, dans cet ordre — comme
    // dans l'annuaire mixte. Sans catégorie (sélection du moment), aucun épinglage.
    const pinnedCandidates = [];
    pools.forEach(pool =>
        pool.candidates.forEach(partner => {
            const order = categoryDisplayOrder(partner, categoryId);
            if (order != null) {
                pinnedCandidates.push({ partner, flags: pool.flags, order });
            }
        })
    );
    pinnedCandidates
        .sort((a, b) => a.order - b.order)
        .forEach(({ partner, flags }) => tryAdd(partner, flags));

    const maxPoolLength = Math.max(...pools.map(pool => pool.candidates.length));
    for (let rank = 0; rank < maxPoolLength && additions.length < neededCount; rank++) {
        pools.forEach(pool => tryAdd(pool.candidates[rank], pool.flags));
    }

    if (additions.length === 0) {
        return validMeas;
    }

    const template = validMeas.length > 0 ? validMeas[0] : meas[0];
    const maxOrder = validMeas.reduce((max, mea) => Math.max(max, mea.ordreAffichage || 0), 0);

    const syntheticMeas = additions.map((addition, index) =>
        createSyntheticMea(addition.partner, index, template, maxOrder, typeId, {
            ...extraProps,
            ...addition.flags
        })
    );

    return [...validMeas, ...syntheticMeas].sort((a, b) => a.ordreAffichage - b.ordreAffichage);
};

/**
 * MEA « par catégorie » (type 97) — règles mixtes ci-dessus, restreintes à
 * la catégorie : candidats filtrés sur categoriesIds (référentiel classique)
 * et categories (référentiel ebon) ; les MEA synthétiques portent
 * l'idRubrique de la catégorie. L'appelant filtre en amont les MEA
 * paramétrés sur leur idRubrique.
 */
export const completeCategoryMeasWithAvailablePartners = (
    categoryMeas = [],
    partners = {},
    ebonPartners = [],
    categoryId,
    targetCount = TARGET_CATEGORY_OFFERS
) =>
    completeMixedMeasWithAvailablePartners(
        categoryMeas,
        partners,
        ebonPartners,
        targetCount,
        CATEGORY_OFFER_MEA_TYPE,
        categoryId,
        { idRubrique: categoryId }
    );

/**
 * MEA « Sélection d'offres du moment » (type 2) — règles mixtes ci-dessus,
 * sans restriction de catégorie : tous types d'offres confondus, complétée
 * avec le top marchand de chaque référentiel.
 */
export const completeMomentMeasWithAvailablePartners = (
    momentMeas = [],
    partners = {},
    ebonPartners = [],
    targetCount = TARGET_MOMENT_OFFERS
) =>
    completeMixedMeasWithAvailablePartners(
        momentMeas,
        partners,
        ebonPartners,
        targetCount,
        SELECTION_MOMENT_MEA_TYPE,
        null,
        {}
    );

/**
 * Complétion générique des MEA mono-référentiel (types 14, 63 et 80) :
 * valide les MEA paramétrés (validationFn), puis complète jusqu'à la cible
 * avec les marchands disponibles (filterFn) les plus populaires.
 * @param {Array} meas - MEA paramétrés renvoyés par le back
 * @param {Object} partners - Map des marchands par id (ou tableau pour ebon)
 * @param {number} targetCount - Nombre d'offres visé
 * @param {Function} validationFn - Valide le marchand d'un MEA paramétré
 * @param {Function} filterFn - Filtre les marchands candidats au complément
 * @param {number} typeId - Type de MEA des MEA synthétiques
 * @param {Object} extraProps - Propriétés ajoutées aux MEA synthétiques
 * @returns {Array} Liste de MEA complétée, triée par ordreAffichage
 */
const completeMeasWithAvailablePartners = (
    meas = [],
    partners = {},
    targetCount,
    validationFn,
    filterFn,
    typeId,
    extraProps = {}
) => {
    // Filter MEAs that have valid active partners
    const validMeas = meas.filter(mea => {
        const partner = Array.isArray(partners)
            ? partners.find(p => p.id === mea.idPartenaire)
            : partners[mea.idPartenaire];
        return validationFn(partner);
    });

    // If we already have enough valid MEAs, return them
    if (validMeas.length >= targetCount) {
        return validMeas;
    }

    // Calculate how many partners we need to add
    const neededCount = targetCount - validMeas.length;

    // Get IDs of partners already used in MEAs
    const usedPartnerIds = new Set(validMeas.map(mea => mea.idPartenaire));

    // Find available partners not already in MEAs
    const partnersArray = Array.isArray(partners) ? partners : Object.values(partners);
    const availablePartners = partnersArray
        .filter(partner => filterFn(partner, usedPartnerIds))
        .sort(sortByRevenue)
        .slice(0, neededCount);

    // If no additional partners available, return what we have
    if (availablePartners.length === 0) {
        return validMeas;
    }

    // Get the template from the first existing MEA (for dates, segment, etc.)
    const template = validMeas.length > 0 ? validMeas[0] : meas[0];

    // Find the max order to continue the sequence
    const maxOrder = validMeas.reduce((max, mea) => {
        return Math.max(max, mea.ordreAffichage || 0);
    }, 0);

    // Create synthetic MEAs for additional partners
    const syntheticMeas = availablePartners.map((partner, index) =>
        createSyntheticMea(partner, index, template, maxOrder, typeId, extraProps)
    );

    // Combine valid MEAs with synthetic ones
    const completedMeas = [...validMeas, ...syntheticMeas];

    // Sort by display order
    return completedMeas.sort((a, b) => a.ordreAffichage - b.ordreAffichage);
};

/**
 * Trie les marchands par chiffre d'affaires (turnoverEbcOrder croissant :
 * plus l'ordre est petit, plus le chiffre d'affaires est élevé). Sans champ
 * (marchands ebon), l'ordre d'origine est conservé (tri stable).
 * @param {Object} a - Premier marchand
 * @param {Object} b - Second marchand
 * @returns {number}
 */
const sortByRevenue = (a, b) => {
    const orderA = a.turnoverEbcOrder || DEFAULT_TURNOVER_ORDER;
    const orderB = b.turnoverEbcOrder || DEFAULT_TURNOVER_ORDER;
    return orderA - orderB;
};

/**
 * Variante « en magasin » de sortByRevenue : l'ordre imposé depuis l'admin B2O
 * (OFFLINE_DISPLAY_ORDER, turnoverEbcOrderOffline négatif, renseigné uniquement
 * pour les marchands priorisés) prime, sinon popularité online — le même ordre
 * que les blocs magasin de l'annuaire mixte (sortOnOrderOffline).
 * @param {Object} a - Premier marchand
 * @param {Object} b - Second marchand
 * @returns {number}
 */
const sortByRevenueOffline = (a, b) => {
    const orderA =
        a.turnoverEbcOrderOffline != null
            ? a.turnoverEbcOrderOffline
            : a.turnoverEbcOrder || DEFAULT_TURNOVER_ORDER;
    const orderB =
        b.turnoverEbcOrderOffline != null
            ? b.turnoverEbcOrderOffline
            : b.turnoverEbcOrder || DEFAULT_TURNOVER_ORDER;
    return orderA - orderB;
};

/**
 * Crée un MEA synthétique pour la complétion automatique : id négatif (le
 * marque comme synthétique), renvoi vers la fiche marchand, ordreAffichage
 * à la suite des MEA paramétrés, dates et segment repris du template.
 * @param {Object} partner - Marchand mis en avant
 * @param {number} index - Index du complément (id et ordre d'affichage)
 * @param {Object} template - MEA modèle pour les dates et le segment
 * @param {number} maxOrder - Dernier ordreAffichage des MEA paramétrés
 * @param {number} typeId - Type de MEA
 * @param {Object} extraProps - Propriétés supplémentaires (flags, rubrique...)
 * @returns {Object} MEA synthétique
 */
const createSyntheticMea = (partner, index, template, maxOrder, typeId, extraProps = {}) => {
    return {
        id: -1000 - index, // Id négatif : marque le MEA comme synthétique
        typeId,
        idPartenaire: partner.id,
        dateDebut: template ? template.dateDebut : Date.now() / 1000,
        dateFin: template ? template.dateFin : Date.now() / 1000 + 86400 * 30,
        ordreAffichage: maxOrder + index + 1,
        segment: template ? template.segment : 0,
        renvoiFiche: true, // Renvoi vers la fiche marchand
        autopromo: false,
        ...extraProps
    };
};



// WEBPACK FOOTER //
// ./src/services/mea/utils.js